import { useCallback, useState } from 'react';
import { LoadingButton } from '@mui/lab';
import { Box, Stack } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import PageLayout from '../components/global/PageLayout';
import PageLoading from '../components/global/PageLoading';
import useUser from '../hooks/global/useUser';
import useGetMarketingData from '../hooks/marketing/useGetMarketingData';
import CampaignsTable from '../components/marketing/CampaignsTable';
import AutomationsPanel from '../components/marketing/AutomationsPanel';
import CampaignEditorModal from '../components/marketing/CampaignEditorModal';
import MarketingCredits from '../components/marketing/MarketingCredits';
import PocketflowsEmbed from '../components/marketing/PocketflowsEmbed';
import useSubscriptionUpsell from '../hooks/subscriptions/useSubscriptionUpsell';
import { Feature } from '../types/subscriptions';
import useMarketingActionGate from '../hooks/marketing/useMarketingActionGate';
import { getAccessLevel } from '../helpers/marketing';
import TabBar from '../components/filtering/TabBar';
import { TabInt } from '../interfaces';
import { MarketingTab } from '../constants/marketing';

function MarketingPage() {
  const { user } = useUser();
  const { data: marketingData, isFetching, isLoading } = useGetMarketingData();
  useSubscriptionUpsell(Feature.MARKETING);
  const runMarketingAction = useMarketingActionGate();
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [params, setParams] = useSearchParams();

  const accessLevel = getAccessLevel(user);
  const embedId = marketingData?.sourceEmbedId;

  const onOpenNewModal = useCallback(() => {
    runMarketingAction(() => setIsNewModalOpen(true));
  }, [runMarketingAction]);

  const campaignsPanel = (
    <Stack spacing={3}>
      <Stack
        direction="row"
        display="flex"
        justifyContent="space-between"
        gap={1}
        alignItems="end"
        flexWrap="wrap">
        <MarketingCredits />
        <LoadingButton
          variant="contained"
          className="primary"
          onClick={onOpenNewModal}>
          <span>New Campaign</span>
        </LoadingButton>
      </Stack>
      <CampaignsTable />
    </Stack>
  );

  const tabs: TabInt[] = [
    {
      id: MarketingTab.CAMPAIGNS,
      label: 'Campaigns',
      component: campaignsPanel,
    },
    {
      id: MarketingTab.AUTOMATIONS,
      label: 'Automations',
      component: <AutomationsPanel />,
    },
    {
      id: MarketingTab.CUSTOM_DOMAIN,
      label: 'Custom Domain',
      component:
        isLoading || !embedId ? (
          <PageLoading />
        ) : (
          <Box
            sx={{
              width: '100%',
              minHeight: { xs: 480, md: '70vh' },
              position: 'relative',
              flex: 1,
            }}>
            <PocketflowsEmbed
              embedId={embedId}
              path="custom_domains"
              title="Custom Domain"
            />
          </Box>
        ),
    },
    {
      id: MarketingTab.BRAND_KIT,
      label: 'Brand Kit',
      component:
        isLoading || !embedId ? (
          <PageLoading />
        ) : (
          <Box
            sx={{
              width: '100%',
              minHeight: { xs: 480, md: '70vh' },
              position: 'relative',
              flex: 1,
            }}>
            <PocketflowsEmbed
              embedId={embedId}
              path="brand_kit"
              title="Brand Kit"
            />
          </Box>
        ),
    },
  ];

  const paramTab = params.get('tab') as MarketingTab | null;
  const selectedTabIndex =
    paramTab && accessLevel === 'full'
      ? Math.max(
          tabs.findIndex(t => t.id === paramTab),
          0,
        )
      : 0;

  const setSelectedTabIndex = (index: number) => {
    const newParams = new URLSearchParams(params);
    const tabId = tabs[index]?.id ?? tabs[0].id;
    if (tabId) newParams.set('tab', String(tabId));
    setParams(newParams);
  };

  return (
    <PageLayout
      title="Marketing"
      isLoading={isFetching}
      guard={{ isAllowed: !!user }}>
      {accessLevel === 'full' ? (
        <TabBar
          tabs={tabs}
          currentTab={selectedTabIndex}
          changeTab={setSelectedTabIndex}
          hideWhenOnlyOneTab
          noTopMargin
          marginTop={3}
        />
      ) : (
        campaignsPanel
      )}
      <CampaignEditorModal
        key={isNewModalOpen.toString()}
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
      />
    </PageLayout>
  );
}

export default MarketingPage;
